// @flow

import './index.scss';
import * as React from 'react';
import classnames from 'classnames';
import { ToastContainer } from 'react-toastify';
import { useTranslate } from '@insolus/react-i18n';
import Link from 'components/Link';
import Logo from 'components/Logo';
import Nav from './Nav';
import SellButton from './SellButton';
import __p from 'utils/__p';

import type { Props as LogoProps } from 'components/Logo';
import type { NavItem } from './Nav';

type StrictNavsObject = {| left?: NavItem[], right?: NavItem[] |};
type NavsObject = NavItem[] | StrictNavsObject;

type Props = {|
    ...LogoProps,
    nav: NavsObject,
    className?: string
|};

const Header = ({ nav, className = '' }: Props): React.Node => {
    const { __ } = useTranslate();
    const [navOpen, setNavOpen] = React.useState<boolean>(false);

    const handleToggleNav = React.useCallback(() => {
        setNavOpen(prevState => !prevState);
    }, []);

    const _navs: StrictNavsObject = Array.isArray(nav) ? { right: nav } : nav;
    const navs = ['left', 'right']
        .map((pos: string): React.Node => {
            const items = _navs[pos];
            if (!items?.length) {
                return null;
            }

            const navClassName = classnames('Header__nav', `Header__nav--${pos}`);
            return <Nav key={pos} className={navClassName} items={items} />;
        })
        .filter(Boolean);

    const navContainerClassName = classnames('Header__navs', {
        'Header__navs--opened': navOpen
    });

    return (
        <header className={classnames(className, { 'Header': true, 'Header--opened': navOpen })}>
            <ToastContainer
                autoClose={3500}
                hideProgressBar
                closeOnClick
                pauseOnVisibilityChange
                pauseOnHover
            />
            <div className="Header__body">
                <div className="Header__content">
                    <div className="Header__main">
                        <div className="Header__logo">
                            <Logo url={__p('/')} />
                            <div className="Header__sell">
                                <SellButton />
                            </div>
                        </div>
                    </div>
                    {navs && (
                        <Link
                            className="Header__toggle Header__toggle--nav"
                            icon="menu"
                            aria-label={__('toggle-navigation')}
                            aria-expanded={navOpen}
                            onClick={handleToggleNav}
                        />
                    )}
                </div>
                {navs && (
                    <nav className={navContainerClassName}>
                        <Link
                            className="Header__close"
                            icon="times"
                            onClick={handleToggleNav}
                        />
                        {navs}
                    </nav>
                )}
            </div>
        </header>
    );
};

export default Header;
